import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { TESTIMONIALS } from "@/lib/content";

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const count = TESTIMONIALS.length;

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" className="bg-[#FFFFFF] py-24 lg:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>In their words</Eyebrow>
              <h2 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
                Clients who found tax season, easier.
              </h2>
            </div>
            <div className="flex items-center gap-5">
              <span className="font-display text-lg italic text-[#102036]/50" data-testid="testimonial-counter">
                {String(selected + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </span>
              <button
                onClick={() => emblaApi?.scrollPrev()}
                data-testid="testimonial-prev-button"
                aria-label="Previous testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-[#102036]/15 text-[#102036] transition-all hover:border-[#3682AE] hover:bg-[#3682AE] hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => emblaApi?.scrollNext()}
                data-testid="testimonial-next-button"
                aria-label="Next testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-[#102036]/15 text-[#102036] transition-all hover:border-[#3682AE] hover:bg-[#3682AE] hover:text-white"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 overflow-hidden" ref={emblaRef} data-testid="testimonial-slider">
            <div className="flex">
              {TESTIMONIALS.map((t) => (
                <figure key={t.name + t.text.slice(0, 20)} className="min-w-0 flex-[0_0_100%] px-1 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                  <div className="flex h-full flex-col rounded-lg border border-[#102036]/10 bg-[#FFFFFF] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(16,32,54,0.07)] sm:p-10">
                    <Quote className="h-8 w-8 text-[#3682AE]/30" aria-hidden="true" />
                    <blockquote className="mt-5 flex-1 text-[15px] leading-relaxed text-[#102036]/85">“{t.text}”</blockquote>
                    <figcaption className="mt-6 border-t border-[#102036]/10 pt-5">
                      <span className="block text-[12px] font-bold uppercase tracking-[0.18em] text-[#102036]">{t.name}</span>
                      {t.tag && <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-[#3682AE]">{t.tag}</span>}
                    </figcaption>
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>

        <p className="mt-10 text-center text-xs leading-relaxed text-[#4A5568]/70">
          Shared with first name or general attribution — full client names are never published without permission.
        </p>
      </div>
    </section>
  );
}
